'use client';

import { useEffect } from 'react';

import { TranslationDetailsType, GuideData, TranslationInfoType } from '@/types/restResponses/guides';
import { StepData } from '@/types/restResponses/steps';

interface LanguageOption {
  locale: string;
  path: string;
}

interface UseLanguageOptionsParams {
  guide: GuideData & {
    translations_info: TranslationInfoType;
  };
  currentHash: string;
  steps: StepData[];
  setLanguageOptions: (options: LanguageOption[]) => void;
}

/**
 * Custom hook for updating language options based on hash changes
 * Updates language options when hash occurs to set correct step
 */
export function useLanguageOptions({ guide, currentHash, steps, setLanguageOptions }: UseLanguageOptionsParams) {
  useEffect(() => {
    if (guide?.translations_info && currentHash !== '' && steps.length > 0) {
      // Get default_id for current hash
      const currentStep = steps.find(step => step.id === parseInt(currentHash));
      const defaultStepId = currentStep
        ? currentStep.default_id
        : steps.find(step => step.default_id === parseInt(currentHash))?.default_id || parseInt(currentHash);

      if (defaultStepId) {
        const translations = Object.entries(guide.translations_info).map(
          ([locale, translation]: [string, TranslationDetailsType]) => ({
            ...translation,
            locale,
          })
        );

        const fullyTranslatedItems = translations.filter(translation => translation.translated);

        const languageOptions = fullyTranslatedItems.map(translation => ({
          locale: translation.locale,
          path: `/${translation.locale}/guide/${translation.slug}#${defaultStepId}`,
        }));

        setLanguageOptions(languageOptions);
      }
    }
  }, [guide?.translations_info, currentHash, setLanguageOptions, steps]);
}
